import type { NextRequest } from 'next/server';
import { buildLaravelApiUrl, buildLaravelMountUrl } from '@/lib/site';
import { buildLaravelBackendUrl } from '@/lib/laravelProxy';

export type SiteStatus = {
  maintenance_mode: boolean;
  maintenance_message?: string | null;
  maintenance_downtime?: string | null;
  maintenance_url?: string;
};

function parseSiteStatus(data: unknown): SiteStatus {
  const row = data as SiteStatus;
  return {
    maintenance_mode: Boolean(row?.maintenance_mode),
    maintenance_message: row?.maintenance_message,
    maintenance_downtime: row?.maintenance_downtime,
    maintenance_url: row?.maintenance_url,
  };
}

/** Direct server → Laravel (uses LARAVEL_API_URL). Used in layouts and route handlers. */
export async function fetchSiteStatusFromBackend(): Promise<SiteStatus> {
  return fetchSiteStatusUrl(buildLaravelBackendUrl('/site-status'));
}

/** Middleware: same-origin proxy, then direct backend URL (LARAVEL_API_URL). */
export async function fetchSiteStatusForMiddleware(request: NextRequest): Promise<SiteStatus> {
  const proxyUrl = new URL('/nx/site-status', request.nextUrl.origin);
  const fromProxy = await fetchSiteStatusUrl(proxyUrl.toString());
  if (fromProxy.maintenance_mode) {
    return fromProxy;
  }
  return fetchSiteStatusFromBackend();
}

async function fetchSiteStatusUrl(url: string): Promise<SiteStatus> {
  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) {
      return { maintenance_mode: false };
    }
    const data = await res.json();
    return parseSiteStatus(data);
  } catch {
    return { maintenance_mode: false };
  }
}

/** @deprecated Prefer fetchSiteStatusFromBackend or fetchSiteStatusForMiddleware */
export async function fetchSiteStatus(): Promise<SiteStatus> {
  return fetchSiteStatusUrl(buildLaravelApiUrl('/site-status'));
}

/** True when response body looks like an HTML document (maintenance page, WAF, 404). */
export function responseBodyLooksLikeHtml(text: string): boolean {
  const t = text.trim();
  return /<\s*!?\s*DOCTYPE/i.test(t) || /<\s*html[\s>]/i.test(t);
}

export function getMaintenanceRedirectUrl(status?: SiteStatus): string {
  const fromApi = status?.maintenance_url?.trim();
  if (fromApi) {
    return fromApi;
  }
  return buildLaravelMountUrl('/maintenance');
}

/** Paths that must stay reachable during maintenance (auth proxy, static assets). */
export function isMaintenanceExemptPath(pathname: string): boolean {
  if (
    pathname.startsWith('/_next') ||
    pathname.startsWith('/nx') ||
    pathname === '/favicon.ico' ||
    pathname === '/robots.txt' ||
    pathname === '/sitemap.xml'
  ) {
    return true;
  }
  return /\.(?:svg|png|jpg|jpeg|gif|webp|ico|woff2?|ttf)$/i.test(pathname);
}
